import { useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { FiberManualRecord, Dashboard, Settings, School } from "@mui/icons-material";
import { ClassDashboardTab } from "./tabs/ClassDashboardTab/ClassDashBoardTab";
import { ClassAttendanceTab } from "./tabs/ClassAttendanceTab/ClassAttendanceTab";
import { useFormContext } from "react-hook-form";
import { useSchoolClass } from "../hooks/useSchoolClass";
import { ClassSettingsTab } from "./tabs/ClassSettingsTab/ClassSettingsTab";

type SchoolClassHookReturn = ReturnType<typeof useSchoolClass>;

export interface SchoolClassDetailProps {
  state: SchoolClassHookReturn['state'];
  actions: SchoolClassHookReturn['actions'];
}

export function SchoolClassDetail({ state, actions }: SchoolClassDetailProps) {
  // Consumimos a aba ativa e os dados diretamente do hook central
  const { activeTab, selectedId, classList, classData } = state;
  const { setActiveTab } = actions;
  
  // Puxamos a função watch do FormProvider que envolve este componente
  const { watch } = useFormContext();

  const isNew = selectedId === 'new';
  
  // O TypeScript já sabe que 'c' é ISchoolClassSidebarProps sem precisarmos tipar na mão
  const currentClassFromSidebar = classList?.find((c) => c.id === selectedId);
  
  // Prioridade de busca do título: Formulário em digitação -> Banco detalhado -> Sidebar -> Fallback
  const title = isNew 
    ? "Nova Turma" 
    : (watch('name') || classData?.name || currentClassFromSidebar?.name || "Carregando...");

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}> 
      
      {/* HEADER: Fixado no topo do container */}
      <Box 
        sx={{
          px: { xs: 2, md: 2 },
          pt: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 2
        }}
      >
        {/* Container flex para alinhar o ícone com o texto */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: { xs: 1, sm: 3 } }}>
          <School sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: 'text.primary', lineHeight: 1 }}>
            {title}
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, newValue: string) => setActiveTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ minHeight: 48 }}
        >
          {/* Escondemos as abas de Presença e Dashboard se a turma for nova */}
          {!isNew && (
            <Tab
              value="presenca"
              icon={<FiberManualRecord sx={{ fontSize: 12, mr: 1, color: 'success.main' }} />}
              iconPosition="start"
              label="Presença ao Vivo"
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            />
          )}
          
          {!isNew && (
            <Tab
              value="dashboard"
              icon={<Dashboard sx={{ fontSize: 20, mr: 1 }} />}
              iconPosition="start"
              label="Dashboard"
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            />
          )}
          
          <Tab
            value="gestao"
            icon={<Settings sx={{ fontSize: 20, mr: 1 }} />}
            iconPosition="start"
            label="Cadastro e Gestão"
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          />
        </Tabs>
      </Box>

      {/* ÁREA DE CONTEÚDO (TabPanel) */}
      <Box 
        role="tabpanel"
        sx={{ 
          pt: { xs: 2, md: 4 },
          pl: { xs: 2, md: 2 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        {activeTab === "gestao" && <ClassSettingsTab state={state} actions={actions} />}
        {activeTab === "dashboard" && <ClassDashboardTab state={state} />}
        {activeTab === "presenca" && <ClassAttendanceTab state={{ 
          attendanceData: state.attendanceData, 
          isFetchingAttendance: state.isFetchingAttendance 
        }} />}
      </Box>

    </Box>
  );
}